import React, { useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { LoyaltyPointsEarningConfiguration } from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewTile from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import { getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import LineBagAltIcon from 'LuxKit/Icons/line/LineBagAltIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import CartReviewTileImageContent from 'components/Pages/CartReviewPage/CartReviewTileImageContent'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import CondensedCartReviewTileContent from 'components/Pages/CartReviewPage/CondensedCartReviewTileContent'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import { useSelector } from '@xstate/react'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import UndoDeletedItemsTile from '../UndoDeletedItemsTile'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import { fetchTransferOptions } from 'actions/ExperienceActions'
import useExperience from 'hooks/Experiences/useExperience'
import { dateIsBeforeOrSame } from 'lib/datetime/dateUtils'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'
import MultiCartParticipantLabel from './MultiCartParticipantLabel'

interface Props {
  multiCartItem: App.MultiCartTransferItem
  className?: string
  hideTitleAndDates?: boolean
  hideAvailabilityDetails?: boolean
  disableOverlay?: boolean
  saveForLaterIfUnavailable?: boolean
  tileVariant?: 'default' | 'condensed'
}

function AirportTransferMultiCartReviewTile({
  multiCartItem,
  className,
  hideTitleAndDates = false,
  hideAvailabilityDetails = false,
  disableOverlay = false,
  saveForLaterIfUnavailable = true,
  tileVariant = 'default',
}: Props) {
  // ============================================================================
  // HOOKS AND CONTEXT
  // ============================================================================
  const intl = useIntl()
  const item = multiCartItem.item as App.Checkout.TransferItem
  const dispatch = useAppDispatch()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()
  const { currentCurrency } = useContext(GeoContext)

  // State selectors
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const transferOptionsState = useAppSelector(state => state.experience.experienceTransferOptions[multiCartItem.experienceId])
  const [experience, experienceLoading, experienceError] = useExperience(multiCartItem.experienceId)

  // ============================================================================
  // STATE CALCULATIONS
  // ============================================================================
  const isDeleted = multiCartItem.state === 'deleted'
  const saveForLater = multiCartItem.state === 'save_for_later'
  const isSoldOut = !!experience && experience?.status !== 'ONLINE'
  const isPast = multiCartItem.transfer.date ? dateIsBeforeOrSame(new Date(multiCartItem.transfer.date), new Date()) : false
  const isBookable = experience?.status === 'ONLINE' && !experienceError && !isPast

  // Loading and error states
  const loading = experienceLoading || !!transferOptionsState?.fetching
  const error = experienceError || transferOptionsState?.error

  // ============================================================================
  // DATA FETCHING EFFECTS
  // ============================================================================
  useEffect(() => {
    dispatch(fetchTransferOptions(multiCartItem.experienceId))
  }, [dispatch, multiCartItem.experienceId, currentCurrency])

  // ============================================================================
  // PRICE SYNCHRONIZATION EFFECT
  // ============================================================================
  useEffect(() => {
    if (!transferOptionsState?.fetching && !transferOptionsState?.error && transferOptionsState?.transfers) {
      const freshTransferOption = transferOptionsState.transfers.find(
        (transfer: App.ExperienceTransferOption) => transfer.id === item.transfer.option?.id,
      )
      const isPriceDifferent = item.transfer.option?.price !== freshTransferOption?.price

      if (freshTransferOption && item.transfer.option && isPriceDifferent) {
        multiCartActorRef.send({
          type: 'update cart item',
          cartId: activeCartId!,
          multiCartItem: {
            ...multiCartItem,
            item: {
              ...item,
              total: freshTransferOption.price,
              transfer: {
                ...item.transfer,
                option: {
                  ...item.transfer.option,
                  price: freshTransferOption.price,
                  memberPrice: freshTransferOption.memberPrice,
                  memberPriceCurrency: freshTransferOption.memberPriceCurrency,
                  taxesAndFees: freshTransferOption.taxesAndFees,
                  discounts: freshTransferOption.discounts,
                },
              },
            },
          },
        })
      }
    }
  }, [transferOptionsState, multiCartActorRef, activeCartId, item, multiCartItem])

  // ============================================================================
  // AUTO SAVE FOR LATER EFFECT
  // ============================================================================
  useEffect(() => {
    if (experience && !isBookable && !saveForLater && !experienceLoading && !isDeleted && saveForLaterIfUnavailable) {
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId!,
        itemIds: [multiCartItem.id],
        saveForLater: true,
        skipNotification: true,
      })
    }
  }, [isBookable, saveForLater, experienceLoading, experience, isDeleted, saveForLaterIfUnavailable, multiCartItem.id, activeCartId, multiCartReviewActorRef])

  // ============================================================================
  // COMPUTED VALUES AND MEMOIZED DATA
  // ============================================================================
  const itemTotals: App.Checkout.ItemViewTotals = useMemo(() => ({
    price: item.transfer.option?.price ?? 0,
    memberPrice: item.transfer.option?.memberPrice ?? 0,
    value: item.transfer.option?.price ?? 0,
    mobileAppDiscount: {
      amount: item.transfer.option?.discounts?.app.amount ?? 0,
      percentage: item.transfer.option?.discounts?.app.percentage ?? 0,
    },
    taxesAndFees: item.transfer.option?.taxesAndFees ?? 0,
    surcharge: 0,
    memberValue: 0,
    extraGuestSurcharge: 0,
  }), [item.transfer.option])

  const {
    publicTotal,
    memberTotal,
  } = useMemo(() => {
    return getUserCartItemDisplayTotals(itemTotals)
  }, [itemTotals])

  const formattedDateRange = useMemo(() => {
    if (!multiCartItem.transfer.date) return ''

    const formattedDate = moment(multiCartItem.transfer.date).format(DMY_CASUAL_FORMAT)
    return multiCartItem.transfer.time ? intl.formatMessage(
      {
        id: 'TJgCYx1Yyy',
        defaultMessage: '{date} · {time}',
        description: 'Airport transfer cart review date and time label',
      },
      {
        date: formattedDate,
        time: multiCartItem.transfer.time,
      },
    ) : formattedDate
  }, [intl, multiCartItem.transfer.date, multiCartItem.transfer.time])

  const experienceOfferPageUrl = useMemo(() => {
    return getExperienceOfferPageURL(experience)
  }, [experience])

  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    return {
      calculationRequests: [
        generateLoyaltyPointsCalculatorExperienceOptions(experience, {}),
      ],
      calculationType: 'exact',
    }
  }, [experience])

  // ============================================================================
  // UI STATE CALCULATIONS
  // ============================================================================
  const bannerTypeVariant = getBannerTypeVariant(isPast, isSoldOut, false, !!error)
  const cancellationInfo = experience?.copy.cancellationInfo
  const image = experience?.images[0]

  const maxCapacityLabel = intl.formatMessage({
    id: '1rXK6HDmq9',
    defaultMessage: 'Max capacity:',
    description: 'Label for maximum capacity in airport transfer cart review tile',
  })

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  const handleSaveForLater = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId!,
      itemIds: [multiCartItem.id],
      saveForLater: !saveForLater,
    })
  }

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!activeCartId) return

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId,
      itemIds: [multiCartItem.id],
      currentStates: {
        [multiCartItem.id]: multiCartItem.state,
      },
    })
  }

  // ============================================================================
  // EARLY RETURNS
  // ============================================================================
  if (loading) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (isDeleted) {
    return <UndoDeletedItemsTile
      title={experience?.name || ''}
      onUndoClick={() => {
        multiCartActorRef.send({
          type: 'undo delete',
          itemIds: [multiCartItem.id],
        })
      }}
    />
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  if (tileVariant === 'condensed') {
    return <CondensedCartReviewTile
      to={experienceOfferPageUrl}
      imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
      content={<CondensedCartReviewTileContent
        title={experience?.name || ''}
        dates={formattedDateRange}
      />}
      price={<CartReviewPriceStack
        price={publicTotal}
        memberPrice={memberTotal}
        value={item.transfer.option?.price ?? 0}
        luxPlusOfferConfig={experience?.luxPlus}
      />}
      banner={<CartReviewBanner
        variant={bannerTypeVariant}
      />}
      onDelete={handleDelete}
    />
  }

  return <CartReviewTile
    to={experienceOfferPageUrl}
    isBookable={isBookable}
    saveForLater={saveForLater}
    imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
    content={<CartReviewTileContent
      title={experience?.name || ''}
      dates={formattedDateRange}
      hideTitleAndDates={hideTitleAndDates}
      packageTitle={multiCartItem.transfer.option?.name}
      cancellationPolicy={cancellationInfo && (
        <ExperienceCancellationText cancellationInfo={cancellationInfo} textSize="medium" />
      )}
      metaData={<Group direction="horizontal" gap={12} verticalAlign="center">
        <BodyTextBlock variant="small" colour="neutral-two">
          {maxCapacityLabel}
        </BodyTextBlock>
        <BodyTextBlock variant="small" colour="neutral-two" startIcon={<LineUsersAltIcon colour="neutral-two" size="S" />}>
          {multiCartItem.transfer.option?.maxPassengers}
        </BodyTextBlock>
        <BodyTextBlock variant="small" colour="neutral-two" startIcon={<LineBagAltIcon colour="neutral-two" size="S" />}>
          {multiCartItem.transfer.option?.maxCheckedBags}
        </BodyTextBlock>
      </Group>}
      loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
      label={<MultiCartParticipantLabel item={multiCartItem} />}
    />}
    price={<CartReviewPriceStack
      price={publicTotal}
      memberPrice={memberTotal}
      value={item.transfer.option?.price ?? 0}
      luxPlusOfferConfig={experience?.luxPlus}
    />}
    banner={<CartReviewBanner
      variant={bannerTypeVariant}
      to={experienceOfferPageUrl}
    />}
    actions={<CartReviewTileActions
      isBookable={isBookable}
      saveForLater={saveForLater}
      onSaveForLater={handleSaveForLater}
      onDelete={handleDelete}
    />}
    className={className}
    hideAvailabilityDetails={hideAvailabilityDetails}
    disableOverlay={disableOverlay}
  />
}

export default AirportTransferMultiCartReviewTile
